import React from "react";
import { Box, Select, Text, UnstyledButton } from "@mantine/core";
import styles from "./SettingsTabs.module.css";

export interface SettingsTabItem {
  /** Stable identifier for the tab (also used for URL sync). */
  value: string;
  /** Visible tab label. */
  label: React.ReactNode;
}

export interface SettingsTabsProps {
  /** Ordered tabs to render. */
  items: SettingsTabItem[];
  /** Currently active tab value (controlled). */
  value: string;
  /** Called with the newly selected tab value. */
  onChange: (value: string) => void;
  /** Accessible name for the tab bar (e.g. "My Business sections"). */
  ariaLabel?: string;
  /** Escape hatch for one-off layout tweaks. */
  className?: string;
}

/**
 * The settings sub-tab bar (design handoff §3.2). On desktop it's a horizontal
 * row of tabs with a bottom rule; the active tab gets an accent underline +
 * accent-ink label. On mobile (≤768px) it collapses to a full-width dropdown so
 * no section hides off-screen behind a horizontal scroll. Controlled — the
 * parent owns the active value (My Business syncs it to the URL `?tab=` param).
 * The desktop tabs are keyboard-navigable per the WAI-ARIA tabs pattern (roving
 * tabindex + arrow / Home / End). Both renderings are always in the DOM; CSS
 * (not JS) shows the right one, so there's no breakpoint flicker.
 */
export default function SettingsTabs({
  items,
  value,
  onChange,
  ariaLabel,
  className,
}: SettingsTabsProps) {
  const tabRefs = React.useRef<(HTMLButtonElement | null)[]>([]);

  const focusTab = (index: number) => {
    const count = items.length;
    if (count === 0) return;
    const next = ((index % count) + count) % count;
    onChange(items[next].value);
    tabRefs.current[next]?.focus();
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault();
        focusTab(index + 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        focusTab(index - 1);
        break;
      case "Home":
        event.preventDefault();
        focusTab(0);
        break;
      case "End":
        event.preventDefault();
        focusTab(items.length - 1);
        break;
      default:
        break;
    }
  };

  const selectData = items.map((item) => ({
    value: item.value,
    label: typeof item.label === "string" ? item.label : item.value,
  }));

  return (
    <Box className={[styles.root, className].filter(Boolean).join(" ")}>
      {/* Desktop: underline tabs */}
      <Box role="tablist" aria-label={ariaLabel} className={styles.tablist}>
        {items.map((item, index) => {
          const active = item.value === value;
          return (
            <UnstyledButton
              key={item.value}
              ref={(el) => {
                tabRefs.current[index] = el;
              }}
              role="tab"
              type="button"
              aria-selected={active}
              tabIndex={active ? 0 : -1}
              className={[styles.tab, active && styles.tabActive]
                .filter(Boolean)
                .join(" ")}
              onClick={() => onChange(item.value)}
              onKeyDown={(event) => handleKeyDown(event, index)}
            >
              <Text component="span" className={styles.label}>
                {item.label}
              </Text>
            </UnstyledButton>
          );
        })}
      </Box>

      {/* Mobile: a dropdown so nothing hides off-screen */}
      <Select
        className={styles.mobileSelect}
        aria-label={ariaLabel || "Section"}
        data={selectData}
        value={value}
        onChange={(v) => v && onChange(v)}
        allowDeselect={false}
        size="md"
        comboboxProps={{ withinPortal: true }}
      />
    </Box>
  );
}
